import type { TaskActivity } from '@/services/api/generated/taskActivityService';
import type { TaskUpdate } from '@/services/types/client-intake-types';

/**
 * Filter type for timeline - determines which events to show.
 * - 'task': Task-specific events + comments (for Updates tab)
 * - 'related': All other events (for Related Activity tab)
 */
export type TimelineFilter = 'task' | 'related';

/**
 * Filter type for note source - determines which items to show based on content type.
 * - 'all': Show all items (default)
 * - 'comments': Show only items with user-written comments (hides standalone events)
 * - 'activity': Show only system/agent-created items (agent notes, events, automated logs)
 */
export type NoteSourceFilter = 'all' | 'comments' | 'activity';

/**
 * Event codes for task-specific events shown in the Updates tab.
 * These are events directly about the task itself.
 */
export const TASK_SPECIFIC_EVENT_CODES = [
  'task_created',
  'task_status_changed',
  'task_assigned',
  'task_due_date_changed',
  'task_priority_changed',
] as const;

export type TaskSpecificEventCode = (typeof TASK_SPECIFIC_EVENT_CODES)[number];

/**
 * Event codes that should be excluded from timeline display.
 * task_update_created/updated are excluded to avoid duplicating comments.
 */
export const EXCLUDED_EVENT_CODES = [
  'task_update_created',
  'task_update_updated',
] as const;

/**
 * Check if an event code is task-specific (belongs in Updates tab).
 */
export function isTaskSpecificEvent(eventCode: string): boolean {
  return TASK_SPECIFIC_EVENT_CODES.includes(eventCode as TaskSpecificEventCode);
}

/**
 * Check if an event code should be excluded from timeline display.
 */
export function isExcludedEvent(eventCode: string): boolean {
  return EXCLUDED_EVENT_CODES.includes(
    eventCode as (typeof EXCLUDED_EVENT_CODES)[number],
  );
}

/**
 * Event codes for task field change events that can be correlated with comments.
 * Used for grouping field changes with their reason comments.
 */
export const FIELD_CHANGE_EVENT_CODES = [
  'task_status_changed',
  'task_assigned',
  'task_due_date_changed',
  'task_priority_changed',
] as const;

export type FieldChangeEventCode = (typeof FIELD_CHANGE_EVENT_CODES)[number];

/**
 * Check if an event code is a field change event that should be grouped with correlated updates.
 */
export function isFieldChangeEvent(
  eventCode: string,
): eventCode is FieldChangeEventCode {
  return FIELD_CHANGE_EVENT_CODES.includes(eventCode as FieldChangeEventCode);
}

/**
 * A standalone TaskUpdate item (comment) not linked to any domain event.
 */
export interface StandaloneUpdateItem {
  type: 'update';
  id: string;
  timestamp: string;
  update: TaskUpdate;
}

/**
 * A standalone domain event not linked to any TaskUpdate comment.
 */
export interface StandaloneEventItem {
  type: 'event';
  id: string;
  timestamp: string;
  activityItem: TaskActivity;
}

/**
 * A correlated group containing a domain event and its linked TaskUpdate comment.
 * These are created when a field change includes a reason/comment.
 */
export interface CorrelatedGroupItem {
  type: 'correlated';
  id: string;
  correlationId: string;
  timestamp: string;
  /** The domain event that triggered the change */
  activityItem: TaskActivity;
  /** The linked TaskUpdate comment with the change reason */
  update: TaskUpdate;
}

/**
 * Union type for all timeline items.
 */
export type TimelineItem =
  | StandaloneUpdateItem
  | StandaloneEventItem
  | CorrelatedGroupItem;

/**
 * Check if a TaskUpdate is from a human user (not an agent/system).
 * A note is from a user if:
 * - agent is null AND user exists (user object or string ID)
 */
export function isUserNote(update: TaskUpdate): boolean {
  return update.agent === null && update.user !== null;
}

/**
 * Check if a TaskUpdate is from an agent/system.
 * A note is from the system if:
 * - agent is set (explicit agent like "bland_caller"), OR
 * - both user and agent are null (background process, displays as "System")
 */
export function isSystemNote(update: TaskUpdate): boolean {
  return update.agent !== null || update.user === null;
}

/**
 * Result type from the useTaskTimeline hook.
 */
export interface TimelineData {
  /** All items sorted by timestamp (newest first) */
  items: TimelineItem[];
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Any error that occurred (from RTK Query) */
  error: unknown;
}
